/**
 * Instanced chips — cylinders rendered in ~1 draw call. We show a small stack at
 * each seated player's chip anchor (cosmetic, driven by roster presence) plus
 * this client's own pending bet stack. The server never sends other players'
 * exact bets, so other stacks are presence-only flourish.
 *
 * Chips are instanced (CLAUDE.md rule) — never one mesh per chip.
 */

import { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { computeSeats, assignSeatIndices, PLAYER_SEAT_COUNT } from './seating';
import { useGameStore } from '../state/store';
import { chipColors } from '../theme/tokens';

const MAX_CHIPS = 320;
/** Max chips in one player's stack (a fat win still reads, without a skyscraper). */
const MAX_STACK = 26;
const CHIP = { radius: 0.07, height: 0.013 } as const;
/** Cents per chip — the lower this is, the TALLER the pile for a given bet. */
const CENTS_PER_CHIP = 55;

/** Pick a chip color for an amount (uses the highest denomination that fits). */
function colorForCents(cents: number): string {
  for (const c of chipColors) {
    if (cents >= c.minCents) return c.color;
  }
  return chipColors[chipColors.length - 1]!.color;
}

export function Chips() {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const geometry = useMemo(
    () => new THREE.CylinderGeometry(CHIP.radius, CHIP.radius, CHIP.height, 24),
    [],
  );
  const colorAttr = useMemo(() => new Float32Array(MAX_CHIPS * 3), []);

  const roster = useGameStore((s) => s.server.roster);
  const players = useMemo(() => roster.filter((r) => r.role === 'player'), [roster]);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    // The 11 fixed painted spots; players occupy a CENTERED subset of them (same
    // mapping as Blobs/cardLayout) so chip stacks land on the painted boxes.
    const seats = computeSeats(PLAYER_SEAT_COUNT);
    const seatIndices = assignSeatIndices(players.length);
    const m = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scale = new THREE.Vector3(1, 1, 1);
    const color = new THREE.Color();

    let idx = 0;
    for (let p = 0; p < players.length && idx < MAX_CHIPS; p++) {
      const seat = seats[seatIndices[p] ?? p];
      if (!seat) continue;
      // Real wager → real stack: height ∝ amount bet (≈ one chip per $1), colored
      // by the bet's denomination tier. No bet placed yet → no chips on the felt.
      const bet = players[p]!.betCents;
      if (bet <= 0) continue;
      const count = Math.max(3, Math.min(MAX_STACK, Math.round(bet / CENTS_PER_CHIP)));
      color.set(colorForCents(bet));
      for (let s = 0; s < count && idx < MAX_CHIPS; s++) {
        pos.set(seat.chipAnchor.x, seat.chipAnchor.y + CHIP.height / 2 + s * CHIP.height, seat.chipAnchor.z);
        m.compose(pos, quat, scale);
        mesh.setMatrixAt(idx, m);
        colorAttr[idx * 3] = color.r;
        colorAttr[idx * 3 + 1] = color.g;
        colorAttr[idx * 3 + 2] = color.b;
        idx++;
      }
    }
    mesh.count = idx;
    mesh.instanceMatrix.needsUpdate = true;
    const ca = geometry.getAttribute('instColor') as THREE.InstancedBufferAttribute | undefined;
    if (ca) ca.needsUpdate = true;
  }, [players, colorAttr, geometry]);

  // Per-instance color via instanced attribute injected into the standard material.
  useLayoutEffect(() => {
    geometry.setAttribute('instColor', new THREE.InstancedBufferAttribute(colorAttr, 3));
  }, [geometry, colorAttr]);

  const onBeforeCompile = useMemo(
    () => (shader: THREE.WebGLProgramParametersWithUniforms) => {
      shader.vertexShader =
        `attribute vec3 instColor;\nvarying vec3 vInstColor;\n` +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>\n vInstColor = instColor;`,
        );
      shader.fragmentShader =
        `varying vec3 vInstColor;\n` +
        shader.fragmentShader.replace(
          '#include <color_fragment>',
          `#include <color_fragment>\n diffuseColor.rgb *= vInstColor;`,
        );
    },
    [],
  );

  return (
    <instancedMesh ref={meshRef} args={[geometry, undefined, MAX_CHIPS]} castShadow receiveShadow frustumCulled={false}>
      <meshStandardMaterial color={'#ffffff'} roughness={0.5} metalness={0.1} onBeforeCompile={onBeforeCompile} />
    </instancedMesh>
  );
}
